import React from 'react';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import { Link } from '@tanstack/react-router';
import { DataTable, StatusBadge, Text } from '@chatsdet/ui';
import { formatDurationMs } from '../../../lib/formatDuration';
import type { RunAreaDto, RunRerunDto } from '../../../features/external-tests/useExternalRun';
import { areaStateBadge } from '../../../features/external-tests/areaSummary';
import type { ExternalShardProgress } from '../../../features/external-tests/externalRunProgressModel';
import { ExternalRunAreaActionsMenu } from './ExternalRunAreaActionsMenu';

const LIVE_PHASE_LABEL: Record<ExternalShardProgress['phase'], string> = {
  queued: 'queued',
  launching: 'launching',
  running: 'running',
  collecting: 'collecting',
  done: 'done',
};

type AreaRow = RunAreaDto & {
  /** The most recent child run that re-ran exactly this area, if any. */
  rerun: RunRerunDto | null;
  /** Live shard phase, present only while this area's `state` is `running`. */
  livePhase: string | null;
};

/**
 * The multi-area run's area grid: one row per shard, shown whenever a run
 * fans out to more than one area. Row click drills into that area's tests
 * (`?area=N`); "Re-run area" launches a fresh run scoped to it.
 */
export function ExternalRunAreas({
  areas,
  reruns,
  liveShards,
  isRunFinished,
  isRerunPending,
  onSelectArea,
  onRerunArea,
  runnerDisabledAreas,
  isSetAreaDisabledPending,
  onToggleAreaDisabled,
  suiteId,
}: {
  areas: RunAreaDto[];
  reruns: RunRerunDto[];
  /** Live per-shard phases from the progress stream, for a still-running run. */
  liveShards: readonly ExternalShardProgress[];
  /** Only a finished (sealed/canceled) run can be re-run one area at a time. */
  isRunFinished: boolean;
  isRerunPending: boolean;
  onSelectArea: (areaIndex: number) => void;
  onRerunArea: (areaIndex: number) => void;
  /**
   * The runner's CURRENT disabled-areas config (by `specPath`), distinct from
   * this run's own `area.state === 'disabled'` snapshot — decides the
   * enable/disable action's label, available whether or not this run finished
   * since it changes the NEXT run, not this one.
   */
  runnerDisabledAreas: string[];
  isSetAreaDisabledPending: boolean;
  onToggleAreaDisabled: (area: RunAreaDto, disabled: boolean) => void;
  /** The enable/disable action needs the suite id; hidden without one (e.g. an ingest-API run). */
  suiteId: string | null;
}) {
  const rerunByArea = React.useMemo(() => {
    const map = new Map<number, RunRerunDto>();
    for (const rerun of reruns) {
      if (rerun.areaIndex == null) continue;
      // Reruns are returned newest-first from the API; keep the first (latest) seen.
      if (!map.has(rerun.areaIndex)) map.set(rerun.areaIndex, rerun);
    }
    return map;
  }, [reruns]);

  const liveByShard = React.useMemo(
    () => new Map(liveShards.map((shard) => [shard.shardIndex, shard])),
    [liveShards]
  );

  const rows = React.useMemo<AreaRow[]>(
    () =>
      areas.map((area) => ({
        ...area,
        rerun: rerunByArea.get(area.index) ?? null,
        livePhase: area.state === 'running' ? (liveByShard.get(area.index)?.phase ?? null) : null,
      })),
    [areas, rerunByArea, liveByShard]
  );

  const columns = React.useMemo<ColumnDef<StockFeatures, AreaRow>[]>(
    () => [
      {
        accessorKey: 'label',
        header: 'Area',
        cell: ({ row }) => {
          const area = row.original;
          return (
            <div className="min-w-0">
              <Text size="sm" weight="medium" className="truncate block">
                {area.label}
              </Text>
              {area.rerun && (
                <Link
                  to="/runs/external/$runId"
                  params={{ runId: area.rerun.runId }}
                  className="text-xs text-muted-foreground hover:underline"
                  onClick={(event) => event.stopPropagation()}
                >
                  {area.rerun.total > 0
                    ? `re-run: ${area.rerun.passed}/${area.rerun.total}${
                        area.rerun.failed === 0 ? ' ✓' : ` · ${area.rerun.failed} failed`
                      }`
                    : `re-run: ${area.rerun.status}`}
                </Link>
              )}
            </div>
          );
        },
        meta: { flex: 1 },
      },
      {
        accessorKey: 'state',
        header: 'State',
        cell: ({ row }) => {
          const area = row.original;
          const style = areaStateBadge(area);
          // A sub-line only earns its place when it adds information beyond
          // the badge itself: `running`/`done` say nothing the "Running"/
          // "Passed"/"Failed" badge doesn't already say, but `queued`,
          // `launching`, and `collecting` are all still-`running` phases the
          // badge alone can't distinguish.
          const livePhase = area.livePhase as ExternalShardProgress['phase'] | null;
          const showLivePhase =
            livePhase === 'queued' || livePhase === 'launching' || livePhase === 'collecting';
          return (
            <div className="flex flex-col gap-0.5">
              <StatusBadge status={style.status} label={style.label} size="sm" />
              {showLivePhase && (
                <Text size="xs" className="text-muted-foreground">
                  {LIVE_PHASE_LABEL[livePhase]}
                </Text>
              )}
              {area.attempt > 1 && (
                <Text size="xs" className="text-muted-foreground">
                  retry {area.attempt - 1}
                </Text>
              )}
            </div>
          );
        },
        meta: { flex: 0.1, minWidth: 120, maxWidth: 140 },
      },
      {
        accessorKey: 'passed',
        header: 'Passed',
        cell: ({ row }) => (
          <Text size="xs" className="text-muted-foreground tabular-nums">
            {row.original.passed}
          </Text>
        ),
        meta: { flex: 0.1, minWidth: 92, maxWidth: 100 },
      },
      {
        accessorKey: 'failed',
        header: 'Failed',
        cell: ({ row }) => (
          <Text
            size="xs"
            className={
              row.original.failed > 0
                ? 'text-status-failed tabular-nums font-medium'
                : 'text-muted-foreground tabular-nums'
            }
          >
            {row.original.failed}
          </Text>
        ),
        meta: { flex: 0.1, minWidth: 92, maxWidth: 100 },
      },
      {
        accessorKey: 'flaky',
        header: 'Flaky',
        cell: ({ row }) => (
          <Text
            size="xs"
            className={
              row.original.flaky > 0
                ? 'text-status-flaky tabular-nums font-medium'
                : 'text-muted-foreground tabular-nums'
            }
          >
            {row.original.flaky}
          </Text>
        ),
        meta: { flex: 0.1, minWidth: 88, maxWidth: 96 },
      },
      {
        accessorKey: 'durationMs',
        header: 'Duration',
        cell: ({ row }) => (
          <Text size="xs" className="text-muted-foreground tabular-nums">
            {row.original.durationMs != null ? formatDurationMs(row.original.durationMs) : '—'}
          </Text>
        ),
        meta: { flex: 0.1, minWidth: 100, maxWidth: 120 },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <ExternalRunAreaActionsMenu
            area={row.original}
            isRunFinished={isRunFinished}
            isRerunPending={isRerunPending}
            onRerunArea={onRerunArea}
            runnerDisabledAreas={runnerDisabledAreas}
            isSetAreaDisabledPending={isSetAreaDisabledPending}
            onToggleAreaDisabled={onToggleAreaDisabled}
            suiteId={suiteId}
          />
        ),
        meta: { flex: 0.1, minWidth: 56, maxWidth: 64 },
      },
    ],
    [
      isRunFinished,
      isRerunPending,
      onRerunArea,
      runnerDisabledAreas,
      isSetAreaDisabledPending,
      onToggleAreaDisabled,
      suiteId,
    ]
  );

  return (
    <div className="mb-4">
      <DataTable
        columns={columns}
        data={rows}
        aria-label="Run areas"
        getRowId={(row) => String(row.index)}
        pageSize={Math.max(rows.length, 1)}
        onRowClick={(row) => onSelectArea(row.index)}
        containerClassName="rounded-t-none border-t-0 filter-lockup-table"
      />
    </div>
  );
}
